import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { Phone, Loader2 } from "lucide-react";

// Set these values directly in the component
// In a real implementation, you might load these from environment variables or query parameters
const DEFAULT_ASSISTANT_ID = '';
const DEFAULT_PHONE_NUMBER_ID = '';
const API_BASE_URL = 'https://www.distributewithease.com/api'; //process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';

const PublicOutboundCall = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [callData, setCallData] = useState({
    customer: {
      number: '',
      name: '',
      numberE164CheckEnabled: false
    }
  });

  const handleChange = (field, value) => {
    const customerField = field.split('.')[1];
    setCallData(prev => ({
      ...prev,
      customer: {
        ...prev.customer,
        [customerField]: value
      }
    }));
  };

  const validatePhoneNumber = (number) => {
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    return phoneRegex.test(number);
  };

  const isFormValid = () => {
    return (
      validatePhoneNumber(callData.customer.number) &&
      callData.customer.name.trim() !== ''
    );
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch(`${API_BASE_URL}/public/outbound-calls`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          assistantId: DEFAULT_ASSISTANT_ID,
          phoneNumberId: DEFAULT_PHONE_NUMBER_ID,
          customer: {
            number: callData.customer.number,
            name: callData.customer.name,
            numberE164CheckEnabled: false
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to initiate call');
      }

      setSuccess(true);
      setCallData({
        customer: {
          number: '',
          name: '',
          numberE164CheckEnabled: false
        }
      });
    } catch (err) {
      setError(err.message || 'An error occurred while initiating the call');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container max-w-lg mx-auto py-8">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-6 h-6" />
            Request a Callback from Jamun Restaurant for Demo of AI Phone Agent
          </CardTitle>
          <CardDescription>
            Enter your information and we'll call you right away
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="mb-6">
              <AlertDescription>Your call has been scheduled! You will receive a call shortly.</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="customerName">Your Name</Label>
                <Input
                  id="customerName"
                  value={callData.customer.name}
                  onChange={(e) => handleChange('customer.name', e.target.value)}
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div>
                <Label htmlFor="customerNumber">Your Phone Number</Label>
                <Input
                  id="customerNumber"
                  type="tel"
                  value={callData.customer.number}
                  onChange={(e) => handleChange('customer.number', e.target.value)}
                  placeholder="Enter phone number (e.g., +1234567890)"
                  required
                  className={!validatePhoneNumber(callData.customer.number) && 
                    callData.customer.number ? 'border-red-500' : ''}
                />
                {!validatePhoneNumber(callData.customer.number) && 
                  callData.customer.number && (
                  <p className="text-sm text-red-500 mt-1">
                    Please enter a valid phone number with country code (e.g., +1234567890)
                  </p>
                )}
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading || !isFormValid()}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Scheduling Call...
                </>
              ) : (
                'Call Me Now'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PublicOutboundCall;
